import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';

import HouseGridImg from '@/assets/images/Landing/house-grid.png';
import HousePlaceholderImg from '@/assets/images/Landing/house-placeholder.png';
import { SigninModal } from '../Auth/SigninModal';
import { MediaObject } from './HeroSection/MediaObject';

const tabs = [
  {
    icon: 'fa-light fa-chart-simple',
    tab: 'Unbeatable Interest Rates',
    part1: {
      heading: 'Unbeatable',
      headingSecondary: 'Interest Rates',
      text: 'Unbeatable Loans shops your rate with artificial intelligence, wholesale mortgage rates, and no mark up.',
    },
    part2: {
      heading: 'Select Your Property With',
      headingSecondary: 'App Extensions',
      text: 'Monitor specific rates for your purchase, refinance, or investment like never before without a formal application or sales funnel.',
    },
  },
  {
    icon: 'fa-light fa-seedling',
    tab: 'Investor Program',
    part1: {
      heading: 'Unbeatable',
      headingSecondary: 'Investor Program',
      text: 'Use Unbeatable Loans to submit cash offers as well as secure fix-and-flip, DSCR, construction and portfolio loans.',
    },
    part2: {
      heading: 'Select Your Investment With our',
      headingSecondary: 'App Extensions',
      text: 'Find your investment on any listing service you use and add it to Unbeatable Loans with our Chrome Extension. Use free and paid market insights from numerous data sources to vet your investment alongside precise loan estimates.',
    },
  },
  {
    icon: 'fa-light fa-house',
    tab: 'Residential Purchase',
    part1: {
      heading: 'Buy a home entirely with',
      headingSecondary: 'Unbeatable Loans',
      text: 'Keep 100% of the buyers agent commission for yourself.',
    },
    part2: {
      heading: 'Select Your Property With',
      headingSecondary: 'App Extensions',
      text: `After adding a home to Unbeatable Loans, make a budget, get approved, and submit an offer. Use the buyers agent's commission to save on closing costs or reduce the purchase price.`,
    },
  },
  {
    icon: 'fa-light fa-hammer',
    tab: 'Refinance',
    part1: {
      heading: 'Unbeatable',
      headingSecondary: 'Refinance',
      text: 'Analyze a refinance with precise loan estimates, weighing the benefits of a lower rate, debt consolidation, or property upgrades.',
    },
    part2: {
      heading: 'Select Your Property With',
      headingSecondary: 'App Extensions',
      text: 'Unbeatables Loans generates precise and actionable loan estimates for your property with live-updates to interest rates. Add your property and pictures with the app or extension and choose refinance as your loan program.',
    },
  },
];

const testimonials = [
  {
    name: 'Chris J.',
    role: 'First-time Homebuyer',
    text: 'Unbeatable Loans allowed me to bring $17,352 less to the closing table without an agent. I saved thousands on closing costs, and now I am a real estate investor because it made it so easy.',
    rating: 5,
  },
  {
    name: 'Michael Chen',
    role: 'Real Estate Investor',
    text: 'The Chrome extension is a game-changer. I can quickly analyze properties with instant loan estimates. Two clicks later and I submitted an offer.',
    rating: 5,
  },
  {
    name: 'Emily Rodriguez',
    role: 'Property Investor',
    text: 'Best tool for wholesale I have ever heard of. The AI-powered offer submission saves me HOURS and 3%.',
    rating: 5,
  },
];

export const LandingTabsSection = () => {
  const theme = useTheme();

  const [tab, setTab] = useState(0);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);

  return (
    <Box
      className="taste-rise"
      sx={{
        position: 'relative',
        background: 'var(--app-shell-gradient)',
        py: { xs: '60px', md: '80px' },
      }}
    >
      <Container sx={{ maxWidth: '1200px' }}>
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: '40px', md: '60px' } }}>
          <Typography
            variant="h2"
            className="font-display"
            sx={{
              color: theme.palette.text.primary,
              fontFamily: 'var(--font-display)',
              fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
              fontWeight: 700,
              letterSpacing: '-0.02em',
              mb: 2,
            }}
          >
            Get{' '}
            <span style={{ color: theme.palette.mainColors.primary }}>
              approved
            </span>
            , submit{' '}
            <span style={{ color: theme.palette.mainColors.primary }}>
              offers
            </span>
            .
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: theme.palette.greyShades.grey1,
              fontSize: { xs: '1rem', md: '1.125rem' },
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Endless, innovative lending programs coupled with offer submission
            and negotiation.
          </Typography>
        </Box>

        {/* Feature Cards */}
        <Grid container spacing={3} sx={{ mb: { xs: '40px', md: '60px' } }}>
          {tabs.map((item, idx) => (
            <Grid item xs={12} sm={6} lg={3} key={idx}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  background:
                    tab === idx
                      ? `linear-gradient(135deg, ${theme.palette.mainColors.primary}15 0%, ${theme.palette.mainColors.primary}05 100%)`
                      : theme.palette.mainColors.background1,
                  border: `1px solid ${
                    tab === idx
                      ? theme.palette.mainColors.primary
                      : theme.palette.greyShades.border1
                  }`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: theme.palette.mainColors.primary,
                    transform: 'translateY(-2px)',
                  },
                }}
                onClick={() => setTab(idx)}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Box sx={{ mb: 2 }}>
                    <i
                      className={`${item.icon}`}
                      style={{
                        fontSize: '2rem',
                        color:
                          tab === idx
                            ? theme.palette.mainColors.primary
                            : theme.palette.greyShades.grey1,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color:
                        tab === idx
                          ? theme.palette.mainColors.primary
                          : theme.palette.text.primary,
                      fontWeight: 600,
                      fontSize: '1rem',
                    }}
                  >
                    {item.tab}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Content Sections */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '60px', md: '80px' },
          }}
        >
          <MediaObject
            heading={tabs[tab].part1.heading}
            headingSecondary={tabs[tab].part1.headingSecondary}
            text={tabs[tab].part1.text}
            img={HousePlaceholderImg}
            buttons={[
              {
                children: 'Get Started',
                onClick: () => setIsSignInModalOpen(true),
              },
            ]}
          />
          <MediaObject
            heading={tabs[tab].part2.heading}
            headingSecondary={tabs[tab].part2.headingSecondary}
            direction="row-reverse"
            text={tabs[tab].part2.text}
            img={HouseGridImg}
            buttons={[
              {
                children: 'Get Started',
                onClick: () => setIsSignInModalOpen(true),
              },
              {
                children: 'Download the chrome extension',
                startIcon: <i className="fa-light fa-store" />,
                onClick: () => {
                  window.open(process.env.VITE_EXTENSION_URL, '_blank');
                },
              },
            ]}
          />
        </Box>

        {/* Testimonials Section */}
        <Box sx={{ mt: { xs: '60px', md: '80px' } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: '40px', md: '60px' } }}>
            <Typography
              variant="h3"
              sx={{
                color: theme.palette.text.primary,
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                fontWeight: 700,
                mb: 2,
              }}
            >
              Trusted by homebuyers and investors
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.greyShades.grey1,
                fontSize: { xs: '1rem', md: '1.125rem' },
                maxWidth: '600px',
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              See what our customers are saying about their experience with
              Unbeatable Loans
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {testimonials.map((testimonial, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Card
                  sx={{
                    height: '100%',
                    background: theme.palette.mainColors.background1,
                    border: `1px solid ${theme.palette.greyShades.border1}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 20px 40px ${theme.palette.mainColors.background}`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ mb: 2 }}>
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <i
                          key={i}
                          className="fa-light fa-star"
                          style={{
                            color: theme.palette.mainColors.primary,
                            marginRight: '4px',
                          }}
                        />
                      ))}
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.greyShades.grey1,
                        fontSize: '1rem',
                        lineHeight: 1.6,
                        mb: 3,
                        fontStyle: 'italic',
                      }}
                    >
                      "{testimonial.text}"
                    </Typography>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: theme.palette.text.primary,
                          fontWeight: 600,
                          fontSize: '1rem',
                        }}
                      >
                        {testimonial.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.greyShades.grey2,
                          fontSize: '0.875rem',
                        }}
                      >
                        {testimonial.role}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
      <SigninModal
        isOpen={isSignInModalOpen}
        onClose={() => setIsSignInModalOpen(false)}
      />
    </Box>
  );
};
